export interface TerminologyReferenceOccurrence {
  tokenCode: string;
  /** UTF-16 offsets, matching native input selectionStart/selectionEnd. */
  start: number;
  end: number;
  rawText: string;
}

export interface TerminologyReferenceEditPlan {
  start: number;
  end: number;
  selectedText: string;
  references: TerminologyReferenceOccurrence[];
  /** True for a recognized linked-wording replacement, rather than plain selected text. */
  isReplacement: boolean;
  blockedReason: string | null;
}

interface DelimiterSpan {
  start: number;
  end: number;
  unclosed?: boolean;
}

/** Recognized syntax only. Registration, permissions and full graph validation remain external. */
export function getTerminologyReferenceOccurrences(
  text: string,
): TerminologyReferenceOccurrence[] {
  return [...text.matchAll(/\{\{\s*([A-Za-z0-9._:-]+)\s*\}\}/g)].map(
    (match) => ({
      tokenCode: match[1].trim().toLowerCase(),
      start: match.index,
      end: match.index + match[0].length,
      rawText: match[0],
    }),
  );
}

const intersects = (
  start: number,
  end: number,
  span: DelimiterSpan,
): boolean =>
  start === end
    ? span.start < start && start < span.end
    : start < span.end && end > span.start;

function normalizeOffset(offset: number, length: number): number {
  if (Number.isNaN(offset)) return 0;
  return Math.min(length, Math.max(0, Math.trunc(offset)));
}

/** Locate questionable delimiter spans conservatively; this is not a reference grammar validator. */
function malformedDelimiterSpans(text: string): DelimiterSpan[] {
  const spans: DelimiterSpan[] = [];
  let start = -1,
    depth = 0,
    position = 0;
  while (position < text.length) {
    if (text.startsWith('{{', position)) {
      if (depth === 0) start = position;
      depth += 1;
      position += 2;
    } else if (text.startsWith('}}', position)) {
      if (depth === 0) spans.push({ start: position, end: position + 2 });
      else {
        depth -= 1;
        if (depth === 0) {
          const end = position + 2;
          if (!/^\{\{\s*[A-Za-z0-9._:-]+\s*\}\}$/.test(text.slice(start, end)))
            spans.push({ start, end });
        }
      }
      position += 2;
    } else position += 1;
  }
  if (depth > 0) spans.push({ start, end: text.length, unclosed: true });
  return spans;
}

/**
 * Plans an edit without changing text. Partial recognized links become whole-link replacements.
 * Questionable delimiter spans require an explicit full-span selection; they are never expanded.
 */
export function planTerminologyReferenceEdit(
  text: string,
  start: number,
  end: number,
): TerminologyReferenceEditPlan {
  const first = normalizeOffset(start, text.length),
    last = normalizeOffset(end, text.length);
  const selectionStart = Math.min(first, last),
    selectionEnd = Math.max(first, last);
  const occurrences = getTerminologyReferenceOccurrences(text);
  const affected = occurrences.filter((occurrence) =>
    intersects(selectionStart, selectionEnd, occurrence),
  );
  const malformed = malformedDelimiterSpans(text);
  const blocked = malformed.some(
    (span) =>
      (intersects(selectionStart, selectionEnd, span) ||
        (span.unclosed &&
          selectionStart === selectionEnd &&
          selectionStart === span.end)) &&
      !(selectionStart <= span.start && selectionEnd >= span.end),
  );
  let plannedStart = selectionStart,
    plannedEnd = selectionEnd;
  if (!blocked)
    for (const occurrence of affected) {
      plannedStart = Math.min(plannedStart, occurrence.start);
      plannedEnd = Math.max(plannedEnd, occurrence.end);
    }
  return {
    start: plannedStart,
    end: plannedEnd,
    selectedText: text.slice(plannedStart, plannedEnd),
    references: affected,
    isReplacement: affected.length > 0,
    blockedReason: blocked
      ? 'Select the entire malformed or incomplete link before replacing it.'
      : null,
  };
}
